import { Platform } from 'react-native';
import uuid from 'react-native-uuid';
import moment from 'moment';
import api from '@/api/axios';
import constants, { isPatientApp } from '@/config/constants';
import { hasOwnProperty } from '@/utils/utils';
import store from '@/stores/store';
import encryptedStorage from '@/stores/encryptedStorage';
import { Session } from './timeService';
import { logError } from './logHandling';

const storage = encryptedStorage();
const deviceUUID = uuid.v4();

let userId: string | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let startDate: number | null = null;
let lastDate: Date | null = null;

let intervalPromise: ReturnType<typeof setInterval> | null = null;

// Set max Idle time after that time tracking stops 1 min
const maxIdleTime = 60 * 1000;

// Set time to get star to 10 minutes
const starTime = 1000 * 60 * 10;

const applyUserId = (id: string) => {
  userId = id;
};

const uploadApi = async (buffer: any, id?: string) => {
  if (store.getState().isPatientDemo || store.getState().isTherapistDemo) {
    return 'demo';
  }
  try {
    const body = {
      practiceSessions: buffer ?? [],
    };
    const url = isPatientApp
      ? `/api/practicesessions`
      : `/api/patients/${id}/practicesessions`;
    const { data } = await api.post(url, body, {
      timeout: 25000,
    });
    return data;
  } catch (error) {
    logError('Could not upload sessions ', error);
    return 'offline';
  }
};

function getDuration(session: Session) {
  return session.endDate - session.startDate;
}

function msToStars(ms: number) {
  return Math.floor(ms / starTime);
}

const touched = (touchedCallback?: (isTimerOver: boolean) => void) => {
  // If timer is over and user touched the screen, start new session, else reset timer
  touchedCallback?.(!timer);
  if (!timer) {
    initInterval();
    startDate = new Date().valueOf();
    timer = setTimeout(timerOver, maxIdleTime);
  } else {
    clearTimeout(timer);
    timer = setTimeout(timerOver, maxIdleTime);
  }
};

const stop = () => {
  return new Promise((resolve, reject) => {
    if (!timer) {
      return resolve('no timer');
    }
    clearTimeout(timer);
    timerOver()
      .then(function () {
        upload().then(() => {
          resolve('success');
        });
      })
      .catch((error) => reject(error));
  });
};

const initInterval = () => {
  // init last Date with current time;
  lastDate = new Date();
  intervalPromise = setInterval(function () {
    const currentDate = new Date();
    const timeDifference = currentDate.getTime() - lastDate.getTime();
    // if time difference is more then 10 seconds stop tracking as the app was in background then
    // also check if time difference is negative (e.g. if user set his clock to the past and stop tracking then)
    if (timeDifference > 10000 || timeDifference < 0) {
      stop();
    } else {
      lastDate = new Date();
    }
  }, 1000);
};

const timerOver = () => {
  timer = null;
  // cancel interval
  clearInterval(intervalPromise);
  return addSession(startDate, lastDate);
};

//add Session to Buffer
const addSession = (startDate: number, endDateObject: Date): Promise<void> => {
  const sessionBufferKey = 'sessionBuffer' + userId;
  const starContainerKey = 'starContainer' + userId;
  const endDate = endDateObject.valueOf();
  const timezoneOffset = endDateObject.getTimezoneOffset() * 60000;

  const uuid = `${deviceUUID}_${startDate}`;

  const newSession = {
    uuid: uuid,
    startDate: startDate,
    endDate: endDate,
  };

  const newSessionWithOffset = {
    uuid: uuid,
    startDate: startDate - timezoneOffset,
    endDate: endDate - timezoneOffset,
    app: constants.appName,
    appVersion: constants.appVersion,
    os: Platform.OS === 'web' ? 'browser' : Platform.OS,
  };

  return new Promise(async (resolve, reject) => {
    try {
      let container = await getStarContainer();
      const key = moment(endDate).format('YYYY-MM-DD');
      const condition = hasOwnProperty(container.store, key);
      if (condition) {
        container.store[moment(endDate).format('YYYY-MM-DD')].duration =
          container.store[moment(endDate).format('YYYY-MM-DD')].duration +
          getDuration(newSession);
      } else {
        container.store[moment(endDate).format('YYYY-MM-DD')] = {
          duration: getDuration(newSession),
        };
      }

      await storage.setItem(starContainerKey, container);
      let buffer = await storage.getItem(sessionBufferKey);
      if (!buffer) {
        buffer = [];
      }
      buffer.push(newSessionWithOffset);
      await storage.setItem(sessionBufferKey, buffer);
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

const getStarContainer = (): Promise<Record<string, any>> => {
  let starContainerKey = 'starContainer' + userId;
  return new Promise(async (resolve, reject) => {
    try {
      let container = await storage.getItem(starContainerKey);
      if (!container) {
        const defaultValue = {
          buffer: [],
          store: {},
          lastProgress: {
            date: moment().format('YYYY-MM-DD'),
            stars: 0,
          },
        };
        container = defaultValue;
        await storage.setItem(starContainerKey, defaultValue);
      }
      resolve(container);
    } catch (error) {
      reject(error);
    }
  });
};

const getStars = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const container = await getStarContainer();
      const starData = {};
      Object.entries(container.store).forEach(([date, duration]) => {
        let stars = msToStars(duration.duration);
        if (stars > 0) {
          starData[date] = stars;
        }
      });
      resolve(starData);
    } catch (error) {
      logError('could not get stars', error);
      reject();
    }
  });
};

const upload = (): Promise<void> => {
  const sessionBufferKey = 'sessionBuffer' + userId;
  const starContainerKey = 'starContainer' + userId;

  return new Promise(async (resolve, reject) => {
    try {
      const buffer = await storage.getItem(sessionBufferKey);

      let data;
      try {
        data = await uploadApi(buffer, userId ?? undefined);
      } catch (error) {
        logError('Could not upload to api', error);
        return;
      }
      if (data !== 'offline' && data !== 'demo') {
        await storage.setItem(sessionBufferKey, []);
        const storedSessions = {};
        data.practiceDurations.forEach(function (duration) {
          duration._id.month = duration._id.month - 1;
          storedSessions[moment(duration._id).format('YYYY-MM-DD')] = {
            duration: duration.duration,
          };
        });
        const container = await getStarContainer();
        container.store = storedSessions;
        await storage.setItem(starContainerKey, container);
      } else {
        await getStarContainer();
      }
      resolve();
    } catch (error) {
      logError('UPLOAD IN TIME SERVICE_', error);
      reject(error);
    }
  });
};

/**
 * Upload results for Eigentraining (therapist and patient)
 */
const uploadResults = async () => {
  if (store.getState().isPatientDemo || store.getState().isTherapistDemo) {
    return;
  }
  const resultsWithKey = store.getState().resultsWithKey;
  const unloadTherapistBuffer = store.getActions().unloadTherapistBuffer;
  const buffer = resultsWithKey[`results${userId}`] ?? [];
  const data = {};
  data.Results = buffer;
  try {
    const result = await api.post('/api/results', data);
    if (result.statusText === 'OK' || result.status === 200) {
      unloadTherapistBuffer({ patientId: userId });
      return {
        success: true,
        bufferPersisted: false,
      };
    }
  } catch (error) {
    logError('Could not upload results', error);
    return {
      success: false,
      bufferPersisted: true,
    };
  }
};

const getTodaysProgress = async () => {
  const starContainerKey = 'starContainer' + userId;

  let showPopup = false;
  let timeToday = 0;

  try {
    const container = await getStarContainer();
    const storedSessions = container.store;
    const condition =
      storedSessions &&
      hasOwnProperty(storedSessions, moment().format('YYYY-MM-DD'));
    if (condition) {
      const operation =
        timeToday + storedSessions[moment().format('YYYY-MM-DD')].duration;
      timeToday = operation;
    }

    //add current timer runtime if running
    if (timer) {
      const timeNow = lastDate.valueOf();
      timeToday = timeToday + (timeNow - startDate);
    }
    let currentProgress = (timeToday % starTime) / starTime;
    let stars = msToStars(timeToday);

    if (
      container.lastProgress.date === moment().format('YYYY-MM-DD') &&
      container.lastProgress.stars < stars
    ) {
      showPopup = true;
      currentProgress = 1;
    }
    // Update lastProgress in storage to save when
    container.lastProgress.date = moment().format('YYYY-MM-DD');
    container.lastProgress.stars = stars;
    await storage.setItem(starContainerKey, container);

    return {
      showPopup: showPopup,
      stars: stars,
      time: timeToday,
      progress: currentProgress,
      error: false,
    };
  } catch (error) {
    logError('Could not fetch progress', error);
    return null;
  }
};

export default {
  applyUserId,
  touched,
  getTodaysProgress,
  upload,
  uploadResults,
  stop,
  getStars,
  getStarContainer,
};
